import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import {
	AppControlClassroomRef,
	createAppControl,
	deleteAppControls,
	NativeAppControlRule,
	updateAppControl,
} from "@/api/classroomAppControls";
import { getClassroomPolicy } from "@/js/api/classroomPolicyApi";

export function classroomAppControlsQueryKey(classroomStableIdV1: string): readonly [string, string] {
	return ["classroomAppControls", classroomStableIdV1] as const;
}

export type ClassroomAppControlsQueryInput = {
	/** Source / SIS id (`selectedClassroomStore.sourceId`), not the teacher-facing display name. */
	classroomName: string;
	classroomStableIdV1: string;
	enabled: boolean;
};

/**
 * App controls have no read endpoint of their own — the only way to fetch the ones applied to a
 * class is the classroom policy response, which carries them alongside the filtering rules.
 */
export function useClassroomAppControlsQuery(input: ClassroomAppControlsQueryInput): UseQueryResult<NativeAppControlRule[]> {
	return useQuery<NativeAppControlRule[], Error>({
		queryKey: classroomAppControlsQueryKey(input.classroomStableIdV1),
		queryFn: async () => {
			const response = await getClassroomPolicy(input.classroomName, input.classroomStableIdV1, true);
			return response.appControls ?? [];
		},
		enabled: input.enabled && Boolean(input.classroomName) && Boolean(input.classroomStableIdV1),
		retry: false,
	});
}

export type CreateAppControlRulesVariables = {
	appControlId: string;
	name: string;
	action: "block" | "allow";
	/** Empty for a whole-class rule; otherwise one row is written per student. */
	studentStableIdV1s: string[];
};

/**
 * Writes one row per student, because app-control storage is keyed by
 * (classroom, student, appControl) and has no multi-student form.
 */
export function useCreateAppControlRulesMutation(
	classroom: AppControlClassroomRef
): UseMutationResult<void, Error, CreateAppControlRulesVariables> {
	const queryClient = useQueryClient();

	return useMutation<void, Error, CreateAppControlRulesVariables>({
		mutationFn: async (variables) => {
			const wholeClass = variables.studentStableIdV1s.length === 0;
			const targets = wholeClass ? [null] : variables.studentStableIdV1s;

			for (const studentStableIdV1 of targets) {
				await createAppControl(classroom, {
					appControlId: variables.appControlId,
					name: variables.name,
					action: variables.action,
					isClassroomRule: wholeClass,
					studentStableIdV1,
				});
			}
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: classroomAppControlsQueryKey(classroom.classroomStableIdV1) });
		},
	});
}

export type UpdateAppControlRulesVariables = {
	/** Every row making up the rule the teacher edited. */
	rows: NativeAppControlRule[];
	action: "block" | "allow";
};

export function useUpdateAppControlRulesMutation(
	classroom: AppControlClassroomRef
): UseMutationResult<void, Error, UpdateAppControlRulesVariables> {
	const queryClient = useQueryClient();

	return useMutation<void, Error, UpdateAppControlRulesVariables>({
		mutationFn: async ({ rows, action }) => {
			for (const row of rows) {
				await updateAppControl(classroom, {
					id: row.id,
					appControlId: row.appControlId,
					name: row.name,
					action,
					isClassroomRule: row.isClassroomRule,
					studentStableIdV1: row.studentStableIdV1 ?? null,
				});
			}
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: classroomAppControlsQueryKey(classroom.classroomStableIdV1) });
		},
	});
}

export function useDeleteAppControlRulesMutation(classroom: AppControlClassroomRef): UseMutationResult<void, Error, string[]> {
	const queryClient = useQueryClient();

	return useMutation<void, Error, string[]>({
		mutationFn: (ids) => deleteAppControls(classroom, ids),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: classroomAppControlsQueryKey(classroom.classroomStableIdV1) });
		},
	});
}
